import { useRouter } from 'next/router'
import { StringifiableRecord, stringifyUrl } from 'query-string'

import { Product } from '../../types/graphql'
import { exclusiveCollectionHandle } from '../utils/domain'

export function collectionHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/collections/${handle}`,
    query,
  })
}

export const useProductOrKitHref = (item: Product) => {
  const category = item.rawCategory
  const productGroupHandle = item.productGroup.handle
  const colorName = item.colorInfo?.name

  const isKit = category === 'Product Kit'

  const query: StringifiableRecord = {
    color: colorName,
  }

  const kitHref = useKitHref(productGroupHandle, query)
  const productHref = useProductHref(productGroupHandle, query)
  const pdpHref = isKit ? kitHref : productHref

  return pdpHref
}

export function useKitHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/kits/${handle}`,
    query,
  })
}

type ProductForHref = {
  productGroupHandle: string
  exclusive: boolean
  genderCategory: string
}
// there are two signatures to support the case where handling exclusive products is not desired or not yet implemented
export function useProductHref(productGroupHandle: string, query: StringifiableRecord): string
export function useProductHref(product: ProductForHref, query: StringifiableRecord): string
export function useProductHref(
  input: string | ProductForHref,
  query: StringifiableRecord = {}
): string {
  const router = useRouter()
  const productGroupHandle = typeof input === 'string' ? input : input.productGroupHandle

  // if the URL is being requested in the context of a specific collection, scope the product to that collection
  // otherwise if the product is an exclusive product, scope the product to the exclusive collection for the product's gender category
  let collectionUrlPrefix = ''
  if (router?.pathname === '/collections/[handle]') {
    collectionUrlPrefix = `/collections/${router?.query.handle}`
  } else if (typeof input === 'object' && input.exclusive) {
    collectionUrlPrefix = `/collections/${exclusiveCollectionHandle(input.genderCategory)}`
  }

  return stringifyUrl({
    url: `${collectionUrlPrefix}/products/${productGroupHandle}`,
    query,
  })
}

export function useProductReviewsHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/products/${handle}/reviews`,
    query,
  })
}

export function useProductWriteReviewHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/products/${handle}/reviews`,
    query: { ...query, 'write-review': true },
  })
}

export function portalCollectionHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/portals/${handle}`,
    query,
  })
}

export function giftProductHref(
  handle: string,
  slug: string,
  giftItemCollectionId: string,
  collectionHandle: string,
  query: StringifiableRecord = {}
) {
  return stringifyUrl({
    url: `/claim-gift/${slug}/collection/${giftItemCollectionId}/${collectionHandle}/product/${handle}`,
    query,
  })
}

export function giftCollectionHref(
  giftItemCollectionId: string,
  collectionHandle: string,
  slug: string,
  query: StringifiableRecord = {}
) {
  return stringifyUrl({
    url: `/claim-gift/${slug}/collection/${giftItemCollectionId}/${collectionHandle}`,
    query,
  })
}

/**
 * Not intended for boolean false param value, since we always return a string.
 * A false param value should return null, but that would ruin the convenience
 * of this convenience function.
 */
export function singleQueryParamValue(
  queryParamValue: string | string[] | null | undefined,
  defaultValue: string = ''
): string {
  if (typeof queryParamValue === 'boolean') return ''
  return (Array.isArray(queryParamValue) ? queryParamValue[0] : queryParamValue) || defaultValue
}
